import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { aiMonitoringAPI } from '../../services';
import PaginationComponent from '../../components/PaginationComponent';

/**
 * Lệnh sếp 26/10/2026 — QC NHẬN DIỆN: trước khi gửi tin vào chủ đề camera-bill, Haiku nhìn ảnh vùng khoanh (viền đỏ)
 * xác nhận có nhóm khách đang ngồi dùng dịch vụ. Tab này thống kê QC đã cho gửi / chặn bao nhiêu, chấm đúng/sai
 * (sếp chấm ở đây; nhân viên xác nhận bill cũng tự chấm) và hiện đúng ảnh QC đã nhìn để sếp kiểm lại.
 */
const PAGE_SIZE = 12;
const EMPTY = { status: '', graded: '', from: '', to: '' };

const STATUS = {
  PASS: { bg: 'success', text: 'Có nhóm · đã cho gửi' },
  FAIL: { bg: 'secondary', text: 'Không có nhóm · đã chặn' },
  ERROR: { bg: 'warning', text: 'Lỗi QC · chưa gửi' },
  DUP: { bg: 'info', text: 'Cùng nhóm vùng khác · không gửi' },
};

const fmt = (v, p = 'HH:mm dd/MM/yyyy') => {
  if (!v) return '—';
  try {
    return format(new Date(v), p);
  } catch {
    return '—';
  }
};

const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

// Ảnh cần JWT nên tải qua apiClient (blob) rồi dựng URL tạm; giải phóng khi thẻ biến mất.
const QcImage = ({ id, branchId }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    let alive = true;
    aiMonitoringAPI
      .getQcImage(id, branchId)
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, branchId]);

  if (failed) return <span className="cambill-img-empty">Không tải được ảnh</span>;
  if (!src)
    return (
      <Spinner
        size="sm"
        animation="border"
        variant="light"
      />
    );
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="cambill-img-wrap"
      title="Mở ảnh lớn"
    >
      <img
        src={src}
        alt="Ảnh QC đã nhìn (viền đỏ = vùng khoanh)"
        loading="lazy"
      />
    </a>
  );
};

const Stat = ({ value, label }) => (
  <div className="cambill-stat">
    <div className="cambill-stat-value">{value}</div>
    <div className="cambill-stat-label">{label}</div>
  </div>
);

const AiQcTab = ({ branchId }) => {
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);

  const [tick, setTick] = useState(0);

  // Tải trong effect, chỉ đặt state sau khi API trả (cờ `loading` bật ở nơi đổi bộ lọc/làm mới).
  useEffect(() => {
    if (!branchId) return undefined;
    let alive = true;
    aiMonitoringAPI
      .getQcChecks({
        branch_id: branchId,
        status: filters.status || undefined,
        graded: filters.graded || undefined,
        from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
        to: filters.to ? new Date(`${filters.to}T23:59:59`).toISOString() : undefined,
        page,
        limit: PAGE_SIZE,
      })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((err) => alive && setError(err?.message || 'Không tải được kết quả QC'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [branchId, filters, page, tick]);

  const load = useCallback(() => {
    setLoading(true);
    setTick((t) => t + 1);
  }, []);

  const change = (patch) => {
    setLoading(true);
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const grade = async (it, correct) => {
    const next = it.truth === correct && it.truth_source === 'ADMIN' ? null : correct;
    try {
      setSaving(it.id);
      await aiMonitoringAPI.gradeQcCheck(it.id, branchId, next);
      load();
    } catch (err) {
      setError(err?.message || 'Không lưu được chấm QC');
    } finally {
      setSaving(null);
    }
  };

  const filtering = Object.values(filters).some(Boolean);
  const items = data?.items ?? [];
  const s = data?.summary;
  const cfg = data?.config;

  if (!branchId) return <p className="text-muted">Chọn chi nhánh để xem kết quả QC.</p>;

  return (
    <div data-testid="qc-nhan-dien">
      <div className="cambill-toolbar">
        <p className="cambill-hint mb-0">
          {cfg
            ? `${cfg.enabled ? 'Đang bật' : 'Đang TẮT'} · ${cfg.model} · tài khoản ${(cfg.accounts || []).join(', ')}. `
            : ''}
          Chỉ khi QC thấy có nhóm khách đang ngồi trong vùng khoanh mới gửi tin vào chủ đề Telegram.
        </p>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={load}
          disabled={loading}
        >
          {loading ? (
            <Spinner
              size="sm"
              animation="border"
            />
          ) : (
            'Làm mới'
          )}
        </Button>
      </div>

      {s && (
        <>
          <div
            className="cambill-stats"
            data-testid="qc-so-lieu"
          >
            <Stat
              value={s.total}
              label={filtering ? 'Lượt QC trong khoảng ngày' : 'Tổng lượt QC'}
            />
            <Stat
              value={s.pass}
              label="Có nhóm · đã cho gửi"
            />
            <Stat
              value={s.fail}
              label="Không có nhóm · đã chặn"
            />
            <Stat
              value={s.dup ?? 0}
              label="Cùng nhóm vùng khác · không gửi"
            />
            <Stat
              value={s.error}
              label="Lỗi QC"
            />
          </div>
          <div
            className="cambill-stats"
            data-testid="qc-do-chinh-xac"
          >
            <Stat
              value={`${s.graded}/${s.pass + s.fail + (s.dup ?? 0)}`}
              label="Đã chấm"
            />
            <Stat
              value={s.correct}
              label="QC đúng"
            />
            <Stat
              value={`${s.wrong} (${s.pass_wrong} gửi nhầm · ${s.fail_wrong} chặn nhầm)`}
              label="QC sai"
            />
            <Stat
              value={pct(s.accuracy)}
              label="Tỉ lệ QC đúng"
            />
          </div>
        </>
      )}

      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col
              xs={12}
              sm={6}
              lg={3}
            >
              <Form.Label htmlFor="qc-loc-ket-qua">Kết luận QC</Form.Label>
              <Form.Select
                id="qc-loc-ket-qua"
                value={filters.status}
                onChange={(e) => change({ status: e.target.value })}
              >
                <option value="">Tất cả</option>
                <option value="PASS">Có nhóm · đã cho gửi</option>
                <option value="FAIL">Không có nhóm · đã chặn</option>
                <option value="DUP">Cùng nhóm vùng khác · không gửi</option>
                <option value="ERROR">Lỗi QC</option>
              </Form.Select>
            </Col>
            <Col
              xs={12}
              sm={6}
              lg={3}
            >
              <Form.Label htmlFor="qc-loc-cham">Chấm</Form.Label>
              <Form.Select
                id="qc-loc-cham"
                value={filters.graded}
                onChange={(e) => change({ graded: e.target.value })}
              >
                <option value="">Tất cả</option>
                <option value="none">Chưa chấm</option>
                <option value="correct">QC đúng</option>
                <option value="wrong">QC sai</option>
              </Form.Select>
            </Col>
            <Col
              xs={6}
              lg={2}
            >
              <Form.Label htmlFor="qc-tu">Từ ngày</Form.Label>
              <Form.Control
                id="qc-tu"
                type="date"
                value={filters.from}
                onChange={(e) => change({ from: e.target.value })}
              />
            </Col>
            <Col
              xs={6}
              lg={2}
            >
              <Form.Label htmlFor="qc-den">Đến ngày</Form.Label>
              <Form.Control
                id="qc-den"
                type="date"
                value={filters.to}
                onChange={(e) => change({ to: e.target.value })}
              />
            </Col>
            <Col
              xs={12}
              lg={2}
              className="d-flex justify-content-lg-end"
            >
              <Button
                variant="link"
                className="px-0"
                onClick={() => change(EMPTY)}
                disabled={!filtering}
              >
                Xoá bộ lọc
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <p className="text-danger">{error}</p>}
      {loading && !data && (
        <div className="text-center py-5">
          <Spinner
            size="sm"
            animation="border"
          />{' '}
          Đang tải...
        </div>
      )}
      {data && !items.length && !loading && (
        <Card className="text-center text-muted py-5">Chưa có lượt QC nào{filtering ? ' khớp bộ lọc' : ''}.</Card>
      )}

      <Row
        xs={1}
        sm={2}
        xl={3}
        className="g-3"
      >
        {items.map((it) => {
          const st = STATUS[it.status] || STATUS.ERROR;
          return (
            <Col key={it.id}>
              <Card
                className="h-100 cambill-card"
                data-testid="the-qc"
              >
                <Card.Body>
                  <div className="cambill-card-head">
                    <span
                      className="cambill-card-title"
                      title={it.region_label || ''}
                    >
                      {it.region_label}
                      {it.table_number != null && (
                        <span className="text-muted fw-normal"> · Bàn {it.table_number}</span>
                      )}
                    </span>
                    <span className="cambill-card-time">{fmt(it.created_at)}</span>
                  </div>
                  <div className="cambill-img-frame">
                    {it.has_image ? (
                      <QcImage
                        id={it.id}
                        branchId={branchId}
                      />
                    ) : (
                      <span className="cambill-img-empty">Lượt này không có ảnh</span>
                    )}
                  </div>
                  <div className="cambill-meta">
                    <Badge bg={st.bg}>{st.text}</Badge>
                    <Badge
                      bg="light"
                      text="dark"
                    >
                      Máy đếm {it.people_count} người
                    </Badge>
                    {it.seated_count != null && (
                      <Badge
                        bg="light"
                        text="dark"
                      >
                        QC: {it.seated_count} người ngồi
                      </Badge>
                    )}
                    {it.notified && <Badge bg="info">Đã gửi tin</Badge>}
                  </div>
                  {(it.reason || it.error) && <div className="cambill-cam">{it.reason || it.error}</div>}
                  <div className="cambill-cam">
                    {[
                      it.camera_serial && `Camera ${it.camera_serial}`,
                      it.account && `${it.account} · ${it.model}`,
                      it.latency_ms != null && `${(it.latency_ms / 1000).toFixed(1)} giây`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {it.status !== 'ERROR' && (
                    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                      <ButtonGroup size="sm">
                        <Button
                          variant={it.truth === true ? 'success' : 'outline-success'}
                          disabled={saving === it.id}
                          onClick={() => grade(it, true)}
                        >
                          QC đúng
                        </Button>
                        <Button
                          variant={it.truth === false ? 'danger' : 'outline-danger'}
                          disabled={saving === it.id}
                          onClick={() => grade(it, false)}
                        >
                          QC sai
                        </Button>
                      </ButtonGroup>
                      {it.truth_source === 'STAFF' && <span className="cambill-cam mt-0">Theo nhân viên xác nhận</span>}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {data && data.total > PAGE_SIZE && (
        <div className="mt-3">
          <PaginationComponent
            totalItems={data.total}
            itemsPerPage={PAGE_SIZE}
            currentPage={page}
            onPageChange={(p) => {
              setLoading(true);
              setPage(p);
            }}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default AiQcTab;
